import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Sparkle, ShieldStar, Copy, ArrowClockwise, PaperPlaneTilt, X,
  Image as ImageIcon, Lock, CheckCircle, WarningCircle,
} from "@phosphor-icons/react";

const TONES = [
  { id: "confident",   label: "Confident",  desc: "Executive-ready, reassuring" },
  { id: "transparent", label: "Transparent",desc: "Honest, willing to discuss gaps" },
  { id: "technical",   label: "Technical",  desc: "Precise, aimed at IT stakeholders" },
];

export default function TrustCenterWizardModal({ client, onClose, onSent }) {
  const [step, setStep] = useState(1);       // 1 preview loading, 2 review, 3 sent
  const [loading, setLoading] = useState(true);
  const [regenBusy, setRegenBusy] = useState({});
  const [sendBusy, setSendBusy] = useState(false);
  const [tone, setTone] = useState("confident");
  const [industry, setIndustry] = useState(client?.industry_hint || "");
  const [preview, setPreview] = useState(null);
  const [narrative, setNarrative] = useState("");
  const [coverB64, setCoverB64] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [error, setError] = useState("");
  const [sendResult, setSendResult] = useState(null);
  const [pwCopied, setPwCopied] = useState(false);

  const fetchPreview = async (opts = {}) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post(
        `/clients/${client.id}/trust-wizard/preview`,
        {
          tone,
          industry_hint: industry || null,
          regenerate_narrative: opts.narrative ?? true,
          regenerate_cover: opts.cover ?? true,
        },
      );
      setPreview(data);
      if (opts.narrative !== false) setNarrative(data.narrative_md || "");
      if (opts.cover !== false && data.cover_image_b64) setCoverB64(data.cover_image_b64);
      if (recipients === "") setRecipients((data.recipients_default || []).join(", "));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPreview(); }, []);

  const regenerate = async (what) => {
    setRegenBusy((b) => ({ ...b, [what]: true }));
    try {
      await fetchPreview({
        narrative: what === "narrative",
        cover: what === "cover",
      });
    } finally {
      setRegenBusy((b) => ({ ...b, [what]: false }));
    }
  };

  const send = async () => {
    setError("");
    setSendBusy(true);
    try {
      const recipList = recipients.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post(
        `/clients/${client.id}/trust-wizard/send`,
        {
          narrative_md: narrative,
          cover_image_b64: coverB64,
          recipients: recipList,
          tone,
        },
      );
      setSendResult(data);
      setStep(3);
      onSent?.(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSendBusy(false);
    }
  };

  const copyPw = () => {
    if (!preview?.generated_password) return;
    navigator.clipboard.writeText(preview.generated_password);
    setPwCopied(true);
    setTimeout(() => setPwCopied(false), 1500);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-40 flex items-start justify-center p-6 overflow-y-auto"
      onClick={onClose} data-testid="trust-wizard-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="sw-card w-full max-w-4xl my-8 flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-brand-cyan/15 text-brand-cyan flex items-center justify-center">
              <ShieldStar size={18} weight="duotone" />
            </div>
            <div>
              <div className="text-white font-semibold text-sm tracking-tight">
                Trust Center Wizard
              </div>
              <div className="text-[11px] text-slate-500">
                {client?.name} · AI-generated 90-day executive report
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="wizard-close-btn"
            className="p-1.5 text-slate-400 hover:text-white rounded transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {step === 3 && sendResult ? (
            <SentSummary sendResult={sendResult} onClose={onClose} />
          ) : (
            <>
              {/* Controls row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Tone" htmlFor="tone">
                  <div className="grid grid-cols-3 gap-1.5" id="tone">
                    {TONES.map((t) => (
                      <button key={t.id} type="button"
                        data-testid={`wizard-tone-${t.id}`}
                        onClick={() => setTone(t.id)}
                        className={`text-left px-2.5 py-2 rounded-md border transition-all ${
                          tone === t.id
                            ? "border-brand-cyan/60 bg-brand-cyan/10"
                            : "border-border hover:border-slate-600"
                        }`}>
                        <div className="text-[13px] text-white font-medium">{t.label}</div>
                        <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{t.desc}</div>
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Industry hint (optional)" htmlFor="industry">
                  <input id="industry" value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    placeholder="healthcare, legal, manufacturing…"
                    data-testid="wizard-industry-input"
                    className="w-full bg-background-deep border border-border rounded px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-brand-cyan transition-colors" />
                </Field>
              </div>

              {/* Cover image */}
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_260px] gap-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Cover art</label>
                    <button onClick={() => regenerate("cover")} disabled={regenBusy.cover || loading}
                      data-testid="wizard-regen-cover-btn"
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-border rounded hover:border-brand-cyan/40 hover:bg-brand-cyan/10 text-slate-300 hover:text-brand-cyan transition-colors">
                      <ArrowClockwise size={11} weight={regenBusy.cover ? "regular" : "bold"} className={regenBusy.cover ? "animate-spin" : ""} />
                      Regenerate
                    </button>
                  </div>
                  <div className="aspect-video w-full rounded-md overflow-hidden border border-border bg-background-deep flex items-center justify-center"
                    data-testid="wizard-cover-preview">
                    {loading || regenBusy.cover ? (
                      <div className="text-xs text-slate-500 flex items-center gap-2">
                        <Sparkle size={14} weight="duotone" className="text-brand-cyan animate-pulse" />
                        Generating cover…
                      </div>
                    ) : coverB64 ? (
                      <img
                        src={`data:image/png;base64,${coverB64}`}
                        alt="Report cover"
                        className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-xs text-slate-500 flex flex-col items-center gap-1.5">
                        <ImageIcon size={22} weight="thin" />
                        No cover — PDF will render without one.
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 block mb-2">
                    90-day snapshot
                  </label>
                  <div className="sw-card p-3.5 text-[12.5px] space-y-2 leading-snug" data-testid="wizard-sla-snapshot">
                    <SnapshotRow label="Overall uptime"
                      value={preview?.sla_summary?.overall_uptime_pct != null
                        ? `${preview.sla_summary.overall_uptime_pct.toFixed(2)}%`
                        : "—"} />
                    <SnapshotRow label="SLA target"
                      value={preview?.sla_target != null ? `${preview.sla_target.toFixed(2)}%` : "—"} />
                    <SnapshotRow label="Incidents closed" value={preview?.sla_summary?.incidents_closed ?? "—"} />
                    <SnapshotRow label="Incidents open" value={preview?.sla_summary?.incidents_open ?? "—"} />
                    <SnapshotRow label="Monitors" value={preview?.sla_summary?.monitors?.length ?? "—"} />
                    <SnapshotRow label="Backup jobs" value={preview?.sla_summary?.backups?.length ?? "—"} />
                  </div>
                </div>
              </div>

              {/* Narrative */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                    AI narrative (edit before sending)
                  </label>
                  <button onClick={() => regenerate("narrative")} disabled={regenBusy.narrative || loading}
                    data-testid="wizard-regen-narrative-btn"
                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-border rounded hover:border-brand-cyan/40 hover:bg-brand-cyan/10 text-slate-300 hover:text-brand-cyan transition-colors">
                    <ArrowClockwise size={11} weight={regenBusy.narrative ? "regular" : "bold"} className={regenBusy.narrative ? "animate-spin" : ""} />
                    Regenerate
                  </button>
                </div>
                <textarea rows={9} value={narrative}
                  onChange={(e) => setNarrative(e.target.value)}
                  data-testid="wizard-narrative-textarea"
                  placeholder={loading ? "Drafting…" : "Write or paste the executive narrative…"}
                  className="w-full bg-background-deep border border-border rounded px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-brand-cyan transition-colors leading-relaxed" />
              </div>

              {/* Share link + password */}
              {preview?.share_url && (
                <div className="sw-card p-4 flex items-start gap-3" data-testid="wizard-share-info">
                  <Lock size={16} weight="duotone" className="text-brand-cyan mt-0.5 shrink-0" />
                  <div className="flex-1 text-[12.5px] text-slate-300 space-y-1.5">
                    <div>
                      <span className="text-slate-500">Live Trust Center:</span>{" "}
                      <a href={preview.share_url} target="_blank" rel="noreferrer"
                        className="text-brand-cyan hover:text-white break-all">
                        {preview.share_url}
                      </a>
                    </div>
                    {preview.generated_password && (
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">Password (just generated, share separately):</span>
                        <code className="text-brand-cyan bg-brand-cyan/10 px-1.5 py-0.5 rounded text-[12px]"
                          data-testid="wizard-share-password">{preview.generated_password}</code>
                        <button onClick={copyPw} data-testid="wizard-copy-password"
                          className="p-1 text-slate-400 hover:text-white">
                          {pwCopied ? <CheckCircle size={13} weight="bold" className="text-emerald-400" /> : <Copy size={13} />}
                        </button>
                      </div>
                    )}
                    {!preview.generated_password && preview.share_has_password && (
                      <div className="text-slate-500 text-[11.5px]">
                        This page already has a password. Share it with the recipient the same way you did before.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Recipients */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 block mb-2">
                  Send PDF to (comma-separated)
                </label>
                <input value={recipients} onChange={(e) => setRecipients(e.target.value)}
                  placeholder="cio@acme.com, ops@acme.com"
                  data-testid="wizard-recipients-input"
                  className="w-full bg-background-deep border border-border rounded px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-brand-cyan transition-colors" />
              </div>

              {error && (
                <div className="text-xs text-red-300 border border-red-500/30 bg-red-500/10 rounded px-3 py-2 flex items-start gap-2" data-testid="wizard-error">
                  <WarningCircle size={14} weight="bold" className="text-red-400 mt-0.5 shrink-0" />
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {step !== 3 && (
          <div className="px-6 py-4 border-t border-border flex items-center justify-between shrink-0 bg-background-deep/40">
            <div className="text-[11px] text-slate-500">
              PDF is built with your edited narrative + cover, then emailed via your SMTP integration.
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onClose} data-testid="wizard-cancel-btn"
                className="px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md text-slate-300">
                Cancel
              </button>
              <button onClick={send} disabled={sendBusy || loading || !narrative.trim() || !recipients.trim()}
                data-testid="wizard-send-btn"
                className="inline-flex items-center gap-2 px-4 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                <PaperPlaneTilt size={14} weight="bold" />
                {sendBusy ? "Sending…" : "Send Trust Report"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor}
        className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 block mb-2">{label}</label>
      {children}
    </div>
  );
}

function SnapshotRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className="text-white font-medium">{value}</span>
    </div>
  );
}

function SentSummary({ sendResult, onClose }) {
  return (
    <div className="text-center py-8" data-testid="wizard-sent-summary">
      <div className="w-16 h-16 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mx-auto mb-4">
        <CheckCircle size={32} weight="fill" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">Trust Report sent</h3>
      <p className="text-sm text-slate-400 max-w-md mx-auto leading-relaxed mb-6">
        Emailed {(sendResult.recipients || []).length} recipient{(sendResult.recipients || []).length === 1 ? "" : "s"} —
        {" "}{Math.round((sendResult.pdf_size_bytes || 0) / 1024)}KB PDF attached.
      </p>
      <div className="sw-card max-w-md mx-auto p-4 text-left text-[12.5px] space-y-2">
        <div>
          <span className="text-slate-500">Recipients:</span>{" "}
          <span className="text-slate-200">{(sendResult.recipients || []).join(", ")}</span>
        </div>
        {sendResult.share_url && (
          <div>
            <span className="text-slate-500">Live Trust Center:</span>{" "}
            <a href={sendResult.share_url} target="_blank" rel="noreferrer"
              className="text-brand-cyan hover:text-white break-all">{sendResult.share_url}</a>
          </div>
        )}
      </div>
      <button onClick={onClose} data-testid="wizard-done-btn"
        className="mt-6 inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">
        Done
      </button>
    </div>
  );
}
